import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SearchIcon,
  PencilIcon,
  RefreshIcon,
  PlusIcon,
  XIcon
} from '@heroicons/react/outline';
import api from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import BookingEditForm from './BookingEditForm';
import BookingTrackingPanel from './BookingTrackingPanel';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatDate, formatDateTime } from '../../utils/formatDate';
import CarrierLogo from './CarrierLogo';

const BookingManagement = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    carrier: '',
    dateFrom: '',
    dateTo: '',
    userEmail: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingDetail, setBookingDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [statusCounts, setStatusCounts] = useState({});
  const [paymentNotice, setPaymentNotice] = useState(null);
  const [editingDetails, setEditingDetails] = useState(false);

  const reqIdRef = useRef(0);

  // Any filter change resets to page 1 so the (server-paginated) results stay coherent.
  const updateFilters = (patch) => {
    setFilters(prev => ({ ...prev, ...patch }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  const resetFilters = (next) => {
    setFilters(next);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Debounced fetch; the reqId guard drops out-of-order responses.
  useEffect(() => {
    const t = setTimeout(fetchBookings, 300);
    return () => clearTimeout(t);
  }, [filters, pagination.page]);

  const fetchBookings = async () => {
    const reqId = ++reqIdRef.current;
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: pagination.limit,
        skip: (pagination.page - 1) * pagination.limit,
        ...(filters.status && { status_filter: filters.status }),
        ...(filters.carrier && { carrier_filter: filters.carrier }),
        ...(filters.search && { search: filters.search }),
        ...(filters.dateFrom && { date_from: filters.dateFrom }),
        ...(filters.dateTo && { date_to: filters.dateTo }),
        ...(filters.userEmail && { user_email: filters.userEmail })
      });

      const response = await api.get(`/admin/bookings?${params}`);
      if (reqId !== reqIdRef.current) return; // superseded by a newer request
      setBookings(response.data.data.bookings);
      setStatusCounts(response.data.data.status_counts || {});
      setPagination(prev => ({ ...prev, total: response.data.data.total_count }));
    } catch (error) {
      if (reqId !== reqIdRef.current) return;
      console.error('Error fetching bookings:', error);
      setError('Failed to load bookings: ' + error.message);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await api.put(`/admin/bookings/${bookingId}/status`, { new_status: newStatus });
      setBookings(prev => prev.map(booking =>
        booking.id === bookingId
          ? { ...booking, status: newStatus }
          : booking
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleViewBooking = async (booking) => {
    setSelectedBooking(booking);
    setBookingDetail(null);
    setPaymentNotice(null);
    setEditingDetails(false);
    setShowViewModal(true);
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/bookings/${booking.id}`);
      setBookingDetail(res.data.data);
    } catch (e) {
      console.error('Error loading booking detail:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  // After a payment action: show what happened and reload the booking, which
  // may now be paid.
  const handlePaymentDone = async (notice) => {
    setPaymentNotice(notice);
    const id = bookingDetail?.id || selectedBooking?.id;
    if (id) {
      try {
        const res = await api.get(`/admin/bookings/${id}`);
        setBookingDetail(res.data.data);
      } catch (e) {
        console.error('Error reloading booking detail:', e);
      }
    }
    fetchBookings();
  };

  // After an edit: say what changed and where the money stands, then reload.
  const handleDetailsSaved = (response) => {
    const r = response?.data || {};
    const parts = [`Saved: ${(r.changes || []).join(', ')}.`];
    if (r.refunded) parts.push(`₹${formatCurrency(r.refunded)} refunded to the customer's wallet.`);
    if (r.amount_due > 0) parts.push(`₹${formatCurrency(r.amount_due)} is now owed.`);
    if (r.link_cancelled) parts.push('The payment link for the old amount was cancelled.');
    const warnings = response?.warnings || [];
    setEditingDetails(false);
    handlePaymentDone({ text: [...parts, ...warnings].join(' '), warn: warnings.length > 0 });
  };

  const handleEditBooking = (booking) => {
    setSelectedBooking(booking);
    setEditStatus(booking.status);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      await updateBookingStatus(selectedBooking.id, editStatus);
      setShowEditModal(false);
      setSelectedBooking(null);
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'delivered': 'bg-green-100 text-green-800',
      'in_transit': 'bg-blue-100 text-blue-800',
      'out_for_delivery': 'bg-purple-100 text-purple-800',
      'pickup_scheduled': 'bg-yellow-100 text-yellow-800',
      'picked_up': 'bg-amber-100 text-amber-800',
      'booked': 'bg-gray-100 text-gray-800',
      'exception': 'bg-red-100 text-red-800',
      'cancelled': 'bg-red-100 text-red-800',
      'failed': 'bg-red-100 text-red-800',
      'returned': 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentColor = (status) => {
    const colors = {
      'paid': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'failed': 'bg-red-100 text-red-800',
      'refunded': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Mirror the backend's accepted set (admin.py update_booking_status valid_statuses).
  const statusOptions = [
    'booked',
    'pickup_scheduled',
    'picked_up',
    'in_transit',
    'out_for_delivery',
    'delivered',
    'returned',
    'cancelled',
    'failed'
  ];

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const fmtDate = formatDate;
  const fmtDateTime = formatDateTime;
  const titleCase = (s) => (s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—');

  // Active-filter chips
  const chipDefs = [
    { key: 'search', label: 'Search', value: filters.search },
    { key: 'userEmail', label: 'User', value: filters.userEmail },
    { key: 'carrier', label: 'Carrier', value: filters.carrier },
    { key: 'dateFrom', label: 'From', value: filters.dateFrom },
    { key: 'dateTo', label: 'To', value: filters.dateTo }
  ].filter(c => c.value);
  const hasActiveFilters = chipDefs.length > 0;

  // Status tabs — counts come from the backend and reflect the other active filters.
  const allCount = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  const extraStatuses = Object.keys(statusCounts).filter(s => !statusOptions.includes(s) && statusCounts[s] > 0);
  const statusTabs = ['all', ...statusOptions, ...extraStatuses];
  const activeTab = filters.status || 'all';

  const inputCls =
    'border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Booking Management</h2>
          <p className="text-xs text-gray-500">View and manage all shipment bookings</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchBookings}
            className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
          >
            <RefreshIcon className="w-4 h-4 mr-1.5" />
            Refresh
          </button>
          {hasPermission('create_booking') && (
            <button
              data-test="new-booking"
              onClick={() => navigate('/admin/bookings/new')}
              className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
            >
              <PlusIcon className="w-4 h-4 mr-1.5" />
              New booking
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Compact filter toolbar */}
      <div className="bg-white shadow-sm rounded-lg p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search AWB, shipment no, name…"
            className={`${inputCls} pl-8 w-full`}
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
          />
        </div>
        <input
          type="text"
          placeholder="User email"
          className={`${inputCls} w-40`}
          value={filters.userEmail}
          onChange={(e) => updateFilters({ userEmail: e.target.value })}
        />
        <select
          className={inputCls}
          value={filters.carrier}
          onChange={(e) => updateFilters({ carrier: e.target.value })}
        >
          <option value="">All carriers</option>
          <option value="XFas">XFas Self Network</option>
          <option value="FedEx">FedEx</option>
          <option value="DHL">DHL</option>
          <option value="UPS">UPS</option>
          <option value="Aramex">Aramex</option>
        </select>
        <input
          type="date"
          className={inputCls}
          value={filters.dateFrom}
          onChange={(e) => updateFilters({ dateFrom: e.target.value })}
          title="From date"
        />
        <input
          type="date"
          className={inputCls}
          value={filters.dateTo}
          min={filters.dateFrom}
          onChange={(e) => updateFilters({ dateTo: e.target.value })}
          title="To date"
        />
      </div>

      {/* Active filter chips */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {chipDefs.map(chip => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-orange-50 border border-orange-200 text-xs text-orange-800"
            >
              <span className="font-medium">{chip.label}:</span> {chip.value}
              <button
                onClick={() => updateFilters({ [chip.key]: '' })}
                className="ml-0.5 rounded-full hover:bg-orange-200 p-0.5"
                title="Remove filter"
              >
                <XIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            onClick={() => resetFilters({ search: '', status: '', carrier: '', dateFrom: '', dateTo: '', userEmail: '' })}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Status tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {statusTabs.map(s => {
          const active = activeTab === s;
          const count = s === 'all' ? allCount : (statusCounts[s] || 0);
          return (
            <button
              key={s}
              onClick={() => updateFilters({ status: s === 'all' ? '' : s })}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${active ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600'}`}
            >
              {s === 'all' ? 'All' : titleCase(s)}
              <span className={`text-[11px] rounded-full px-1.5 py-0.5 font-semibold ${active ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Bookings Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">
            Bookings <span className="text-gray-400 font-normal">({pagination.total})</span>
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-2">Shipment</th>
                  <th className="px-4 py-2">Route</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Carrier</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {bookings.map((booking) => (
                  <tr
                    key={booking.id}
                    onClick={() => handleViewBooking(booking)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{booking.shipment_number}</span>
                      <span className="ml-2 text-xs text-gray-400">{booking.awb}</span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                      <span title={`${booking.sender_city || ''} → ${booking.recipient_city || ''}`}>
                        {booking.sender_name}
                        <span className="text-gray-400 mx-1">→</span>
                        {booking.recipient_name}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <select
                        value={booking.status}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                        className={`text-xs font-medium rounded-full pl-2 pr-6 py-1 border-0 cursor-pointer focus:ring-1 focus:ring-orange-400 ${getStatusColor(booking.status)}`}
                        title="Change status"
                      >
                        {[...new Set([...statusOptions, booking.status])].filter(Boolean).map(status => (
                          <option key={status} value={status}>{titleCase(status)}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                      <div className="flex items-center gap-2">
                        <CarrierLogo carrier={booking.carrier} />
                        <span>{booking.carrier}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {booking.amount_due && booking.payment_status === 'paid' ? (
                        <span title="Part paid; the rest is still owed">
                          ₹{formatCurrency(booking.amount)}
                          <span className="block text-[11px] font-semibold text-amber-700">+₹{formatCurrency(booking.amount_due)} owed</span>
                        </span>
                      ) : booking.amount_due ? (
                        <span title="Booked, awaiting payment">
                          ₹{formatCurrency(booking.amount_due)}
                          <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">DUE</span>
                        </span>
                      ) : (
                        <>₹{formatCurrency(booking.amount)}</>
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      {fmtDate(booking.created_at)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditBooking(booking); }}
                        className="text-gray-500 hover:text-gray-900"
                        title="Edit status"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">
                      No bookings found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="bg-white px-4 py-2.5 flex items-center justify-between border-t border-gray-200">
          <p className="text-xs text-gray-600">
            {pagination.total > 0 ? (
              <>
                Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span>
                –<span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
                {' '}of <span className="font-medium">{pagination.total}</span>
              </>
            ) : '—'}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
              disabled={pagination.page === 1}
              className="px-3 py-1 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              className="px-3 py-1 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* View Booking Modal — full detail */}
      {showViewModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4">
          {/* Wide: the booking on the left as before, its tracking on the right. */}
          <div className="relative my-8 w-full max-w-7xl bg-white rounded-xl shadow-xl" data-test="booking-modal">
            {/* Sticky header */}
            <div className="flex justify-between items-start px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {bookingDetail?.shipment_number || selectedBooking?.shipment_number}
                  </h3>
                  {(bookingDetail?.status || selectedBooking?.status) && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(bookingDetail?.status || selectedBooking?.status)}`}>
                      {titleCase(bookingDetail?.status || selectedBooking?.status)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  AWB {bookingDetail?.carrier_info?.tracking_number || selectedBooking?.awb || '—'}
                  {' · '}Booked {fmtDateTime(bookingDetail?.created_at || selectedBooking?.created_at)}
                </p>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="lg:flex">
            <div className="flex-1 min-w-0 px-6 py-5 max-h-[70vh] overflow-y-auto" data-test="booking-detail-left">
              {detailLoading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : !bookingDetail ? (
                <p className="text-sm text-gray-500">Could not load full details for this booking.</p>
              ) : editingDetails ? (
                <BookingEditForm
                  booking={bookingDetail}
                  onCancel={() => setEditingDetails(false)}
                  onSaved={handleDetailsSaved}
                />
              ) : (
                <div className="space-y-4">
                  {paymentNotice && (
                    <div
                      data-test="payment-notice"
                      className={`rounded-md border px-3 py-2 text-sm ${paymentNotice.error ? 'border-red-200 bg-red-50 text-red-700' : paymentNotice.warn ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-green-200 bg-green-50 text-green-800'}`}
                    >
                      {paymentNotice.text}
                    </div>
                  )}
                  {bookingDetail.amount_due > 0 &&
                    (hasPermission('create_booking') || hasPermission('record_payments')) && (
                    <PaymentPanel
                      key={bookingDetail.id}
                      d={bookingDetail}
                      canLink={hasPermission('create_booking')}
                      canRecord={hasPermission('record_payments')}
                      fmtDate={fmtDate}
                      titleCase={titleCase}
                      onDone={handlePaymentDone}
                    />
                  )}
                  <DetailBody
                    d={bookingDetail}
                    fmtDate={fmtDate}
                    fmtDateTime={fmtDateTime}
                    titleCase={titleCase}
                    getPaymentColor={getPaymentColor}
                  />
                </div>
              )}
            </div>
            {bookingDetail && !detailLoading && (
              <div className="lg:w-[400px] lg:flex-shrink-0 border-t lg:border-t-0 lg:border-l border-gray-200 bg-gray-50 px-5 py-5 lg:max-h-[70vh] overflow-y-auto">
                <BookingTrackingPanel d={bookingDetail} fmtDateTime={fmtDateTime} titleCase={titleCase} />
              </div>
            )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
              {bookingDetail && !editingDetails && hasPermission('edit_booking') &&
                !['draft', 'cancelled'].includes(bookingDetail.status) && (
                <button
                  data-test="edit-booking"
                  onClick={() => { setPaymentNotice(null); setEditingDetails(true); }}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-800 text-sm rounded-md hover:bg-gray-50"
                >
                  Edit booking
                </button>
              )}
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-800 text-sm rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Booking Modal */}
      {showEditModal && selectedBooking && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-24 mx-auto p-5 w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-semibold text-gray-900">Edit Booking Status</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-xs text-gray-500">Shipment</span>
                <p className="text-sm text-gray-900">{selectedBooking.shipment_number}</p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">New status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                >
                  {[...new Set([...statusOptions, editStatus])].filter(Boolean).map(status => (
                    <option key={status} value={status}>{titleCase(status)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-5 flex justify-end space-x-2">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-800 text-sm rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---- Getting paid what a booking owes ---- */

const OFFLINE_METHODS = [
  ['cash', 'Cash'],
  ['bank_transfer', 'Bank transfer'],
  ['upi', 'UPI'],
  ['cheque', 'Cheque'],
  ['other', 'Other'],
];

// For any booking with money still owed - unpaid, part-paid ("pay at pickup")
// or repriced upwards: record a payment the customer made to us directly, or
// send them a link to pay online. The server counts each payment once, however
// it comes, so a link paid before a payment is recorded is never counted twice.
const PaymentPanel = ({ d, canLink, canRecord, fmtDate, titleCase, onDone }) => {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [recording, setRecording] = useState(false);
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [copied, setCopied] = useState(false);

  const link = d.payment_link;
  const linkLive = !!link && ['created', 'partially_paid'].includes(link.status) && !link.counted;
  const due = d.amount_due;
  const unpaid = d.payment_info?.status === 'pending';
  const partPaid = d.payment_info?.payment_method === 'partial';

  const run = async (key, call, describe) => {
    setBusy(key);
    setError(null);
    try {
      const res = await call();
      await onDone({ text: describe(res.data) });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'That did not work. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const sendLink = () => run(
    'link',
    () => api.post(`/admin/bookings/${d.id}/payment-link`),
    (data) => `${data.message} ${data.data?.short_url || ''}`.trim(),
  );

  const checkLink = () => run(
    'check',
    () => api.post(`/admin/bookings/${d.id}/payment-link/check`),
    ({ data }) => {
      if (data?.paid) return data.newly_paid ? 'Paid online, and counted on the booking.' : "That link's payment is already counted.";
      if (data?.status === 'underpaid') return 'The link was paid, but for less than was owed. It has not been counted.';
      if (data?.status === 'nothing_due') return 'The link was paid, but nothing was owed by then. It is flagged as a payment issue.';
      return `Not paid yet (link ${titleCase(data?.status || 'unknown').toLowerCase()}).`;
    },
  );

  const recordPayment = () => run(
    'record',
    () => api.post(`/admin/bookings/${d.id}/record-payment`, {
      offline_method: method,
      reference: reference.trim() || null,
    }),
    (data) => [data.message, ...(data.warnings || [])].filter(Boolean).join(' '),
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.short_url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const btn = 'px-3 py-1.5 rounded-md text-xs font-medium disabled:opacity-50';

  return (
    <div data-test="payment-panel" className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-amber-900">
            {unpaid ? `Awaiting payment of ₹${formatCurrency(due)}` : `₹${formatCurrency(due)} still owed`}
          </p>
          <p className="text-xs text-amber-800">
            {unpaid
              ? 'The booking is confirmed; the customer has not paid for it yet.'
              : partPaid
                ? `Paid ₹${formatCurrency(d.paid_so_far)} up front (pay at pickup); the rest is due on delivery.`
                : `Paid ₹${formatCurrency(d.paid_so_far)} so far; the price is now ₹${formatCurrency(d.final_cost)}.`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canLink && (
            <button type="button" data-test="send-link" onClick={sendLink} disabled={!!busy}
              className={`${btn} bg-white border border-amber-300 text-amber-900 hover:bg-amber-100`}>
              {busy === 'link' ? 'Sending...' : linkLive ? 'Send link again' : 'Send payment link'}
            </button>
          )}
          {canLink && link?.id && (
            <button type="button" data-test="check-link" onClick={checkLink} disabled={!!busy}
              className={`${btn} bg-white border border-amber-300 text-amber-900 hover:bg-amber-100`}>
              {busy === 'check' ? 'Checking...' : 'Check payment'}
            </button>
          )}
          {canRecord && (
            <button type="button" data-test="record-toggle" onClick={() => setRecording((r) => !r)} disabled={!!busy}
              className={`${btn} bg-orange-600 text-white hover:bg-orange-700`}>
              Record payment
            </button>
          )}
        </div>
      </div>

      {link?.short_url && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-amber-900">
          <span>
            Payment link ({titleCase(link.status || 'created').toLowerCase()}
            {link.expire_by ? `, until ${fmtDate(new Date(link.expire_by * 1000).toISOString())}` : ''}):
          </span>
          <code data-test="link-url" className="rounded bg-white px-1.5 py-0.5">{link.short_url}</code>
          <button type="button" onClick={copy} className="underline">{copied ? 'Copied' : 'Copy'}</button>
        </div>
      )}

      {d.payment_issue && (
        <p className="mt-2 text-xs text-red-700">
          Razorpay reported ₹{formatCurrency(d.payment_issue.amount_paid)} paid against
          {' '}₹{formatCurrency(d.payment_issue.amount_due)} due. Check the payment before recording anything.
        </p>
      )}

      {recording && canRecord && (
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <select data-test="record-method" value={method} onChange={(e) => setMethod(e.target.value)}
            className="border border-gray-300 rounded-md px-2.5 py-1.5 text-sm bg-white">
            {OFFLINE_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input data-test="record-reference" value={reference} onChange={(e) => setReference(e.target.value)}
            placeholder="Reference (optional)" maxLength={100}
            className="border border-gray-300 rounded-md px-2.5 py-1.5 text-sm" />
          <button type="button" data-test="record-confirm" onClick={recordPayment} disabled={!!busy}
            className={`${btn} bg-green-600 text-white hover:bg-green-700 text-sm`}>
            {busy === 'record' ? 'Recording...' : `Confirm ₹${formatCurrency(due)} received`}
          </button>
          {linkLive && (
            <p className="sm:col-span-3 text-[11px] text-amber-800">
              The payment link will be cancelled, so the customer cannot pay twice.
            </p>
          )}
        </div>
      )}

      {error && <p className="mt-2 text-xs text-red-700" role="alert">{error}</p>}
    </div>
  );
};

/* ---- Detail modal body ---- */

const Field = ({ label, value, mono }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-sm text-gray-900 break-words ${mono ? 'font-mono text-xs' : ''}`}>{value}</div>
    </div>
  );
};

const Section = ({ title, icon, children, accent }) => (
  <div className={`rounded-lg border ${accent || 'border-gray-200'} p-4`}>
    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
      {icon}
      {title}
    </h4>
    {children}
  </div>
);

const AddressBlock = ({ a }) => {
  if (!a) return <p className="text-sm text-gray-400">—</p>;
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
      <Field label="Name" value={a.name} />
      <Field label="Company" value={a.company} />
      <Field label="Phone" value={a.phone} />
      <Field label="Email" value={a.email} />
      <div className="col-span-2">
        <Field
          label="Address"
          value={[a.street, a.landmark].filter(Boolean).join(', ')}
        />
      </div>
      <Field label="City" value={a.city} />
      <Field label="State" value={a.state} />
      <Field label="Postal Code" value={a.postal_code} />
      <Field label="Country" value={a.country} />
    </div>
  );
};

const SummaryCell = ({ label, children }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">{label}</div>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

const DetailBody = ({ d, fmtDate, fmtDateTime, titleCase, getPaymentColor }) => {
  const pkg = d.package_info || {};
  const dim = pkg.dimensions || {};
  const carrier = d.carrier_info || {};
  const pay = d.payment_info || {};
  const user = d.user || {};
  const yesNo = (b) => (b ? 'Yes' : 'No');
  // Nothing is recorded as paid until it is, so an unpaid booking's amount is
  // its price, shown as due.
  const unpaid = pay.status === 'pending' && d.final_cost != null && !['draft', 'cancelled'].includes(d.status);
  const shownAmount = unpaid ? d.final_cost : pay.amount;
  const override = d.price_override;

  return (
    <div className="space-y-4">
      {/* Key summary bar — the most-scanned facts up top */}
      <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-orange-50/70 to-white px-4 py-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
          <SummaryCell label="Route">
            <span className="font-medium">{d.sender?.city || '—'}</span>
            <span className="text-gray-400 mx-1">→</span>
            <span className="font-medium">{d.recipient?.city || '—'}</span>
          </SummaryCell>
          <SummaryCell label="Carrier">
            <div className="flex items-center gap-2">
              <CarrierLogo carrier={carrier.carrier_name} />
              <span className="font-medium truncate">{carrier.carrier_name || '—'}</span>
            </div>
            {carrier.service_type && <div className="text-xs text-gray-500 mt-0.5">{titleCase(carrier.service_type)}</div>}
          </SummaryCell>
          <SummaryCell label={unpaid ? 'Amount due' : 'Amount'}>
            <span className="text-lg font-bold text-gray-900">
              {pay.currency === 'USD' ? '$' : '₹'}{shownAmount != null ? shownAmount : '—'}
            </span>
          </SummaryCell>
          <SummaryCell label="Payment">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPaymentColor(pay.status)}`}>
              {titleCase(pay.status)}
            </span>
            {pay.payment_method && <div className="text-xs text-gray-500 mt-0.5">{titleCase(pay.payment_method)}</div>}
          </SummaryCell>
        </div>
      </div>

      {/* Customer + Overview */}
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Customer (booked by)">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label="Name" value={[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'} />
            <Field label="Account type" value={titleCase(user.user_type)} />
            <Field label="Email" value={user.email} />
            <Field label="Phone" value={user.phone} />
            <Field label="Company" value={user.company_name} />
          </div>
        </Section>
        <Section title="Shipment">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label="Shipment No" value={d.shipment_number} />
            <Field label="AWB / Tracking" value={carrier.tracking_number} mono />
            <Field label="Type" value={titleCase(pkg.type)} />
            <Field label="Status" value={titleCase(d.status)} />
            <Field label="Booked on" value={fmtDateTime(d.created_at)} />
            <Field label="Last updated" value={fmtDateTime(d.updated_at)} />
            <Field label="Reference" value={d.custom_reference} />
            <Field
              label="Booked by our team"
              value={d.created_via === 'admin' ? (d.created_by_admin_email || 'Yes') : null}
            />
          </div>
        </Section>
      </div>

      {/* Sender + Recipient */}
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Sender">
          <AddressBlock a={d.sender} fmtDate={fmtDate} />
        </Section>
        <Section title="Recipient (destination)">
          <AddressBlock a={d.recipient} fmtDate={fmtDate} />
        </Section>
      </div>

      {/* Package & contents */}
      <Section title="Package & contents">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
          <Field label="Contents" value={pkg.contents_description} />
          <Field label="Quantity" value={pkg.quantity} />
          <Field label="Declared value" value={pkg.declared_value != null ? `₹${pkg.declared_value}` : null} />
          <Field
            label="Dimensions (L×W×H)"
            value={(dim.length || dim.width || dim.height) ? `${dim.length || '?'} × ${dim.width || '?'} × ${dim.height || '?'} cm` : null}
          />
          <Field label="Actual weight" value={dim.weight != null ? `${dim.weight} kg` : null} />
          <Field label="Volumetric weight" value={d.volumetric_weight != null ? `${d.volumetric_weight} kg` : null} />
          <Field label="Chargeable weight" value={d.chargeable_weight != null ? `${d.chargeable_weight} kg` : null} />
          <Field label="Fragile" value={yesNo(pkg.fragile)} />
          <Field label="Dangerous goods" value={yesNo(pkg.dangerous_goods)} />
          <Field label="Insurance" value={d.insurance_required ? (d.insurance_amount != null ? `Yes · ₹${d.insurance_amount}` : 'Yes') : 'No'} />
          <Field label="Signature required" value={yesNo(d.signature_required)} />
        </div>
      </Section>

      {/* Carrier + Payment */}
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Carrier & service">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Carrier</div>
              <div className="text-sm text-gray-900 flex items-center gap-2 mt-0.5">
                <CarrierLogo carrier={carrier.carrier_name} />
                <span>{carrier.carrier_name || '—'}</span>
              </div>
            </div>
            <Field label="Service" value={titleCase(carrier.service_type)} />
            <Field label="Carrier reference" value={carrier.carrier_reference} mono />
            <Field label="Est. delivery" value={carrier.estimated_delivery ? fmtDate(carrier.estimated_delivery) : null} />
          </div>
        </Section>
        <Section title="Payment" accent="border-gray-200">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-400">{unpaid ? 'Amount due' : 'Amount'}</div>
              <div className="text-sm font-semibold text-gray-900">
                {pay.currency === 'USD' ? '$' : '₹'}{shownAmount != null ? shownAmount : '—'}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Payment status</div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${getPaymentColor(pay.status)}`}>
                {titleCase(pay.status)}
              </span>
            </div>
            <Field label="Method" value={titleCase(pay.payment_method)} />
            <Field label="Paid by" value={pay.offline_method ? titleCase(pay.offline_method) : null} />
            <Field label="Currency" value={pay.currency} />
            <Field label="Payment ID" value={pay.payment_id} mono />
            <Field label="Transaction ID" value={pay.transaction_id} mono />
            <Field label="Final cost" value={d.final_cost != null ? `₹${d.final_cost}` : null} />
            <Field
              label="Pickup"
              value={d.pickup_method === 'drop_off'
                ? 'Drop-off at our office (free)'
                : d.pickup_method === 'pickup'
                  ? `Door pickup · ₹${formatCurrency(d.pickup_charge)}`
                    + (d.pickup_weight ? ` (${d.pickup_weight} kg × ₹${d.pickup_rate}/kg)` : '')
                    + (d.pickup_pincode ? ` · PIN ${d.pickup_pincode}` : '')
                  : null}
            />
            <Field label="Amount paid" value={d.actual_payment_amount != null ? `₹${d.actual_payment_amount}` : null} />
            <Field label="Still owed" value={!unpaid && d.amount_due > 0 ? `₹${formatCurrency(d.amount_due)}` : null} />
          </div>
          {override && (
            <p data-test="price-override" className="mt-3 rounded-md bg-orange-50 px-3 py-2 text-xs text-orange-900">
              Price changed from ₹{override.quoted_price} to ₹{override.price}
              {override.by_email ? ` by ${override.by_email}` : ''}: {override.reason}
            </p>
          )}
          {(d.additional_payments || []).length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-gray-700">
              {d.additional_payments.map((p) => (
                <li key={p.id}>
                  + ₹{formatCurrency(p.amount)} received{' '}
                  {p.method === 'payment_link' ? 'online (payment link)' : `by ${titleCase(p.offline_method || p.method).toLowerCase()}`}
                  {p.transaction_id ? ` · ${p.transaction_id}` : ''} · {fmtDateTime(p.at)}
                </li>
              ))}
            </ul>
          )}
          {(d.refunds || []).length > 0 && (
            <ul data-test="refunds" className="mt-3 space-y-1 text-xs">
              {d.refunds.map((r) => (
                <li key={r.id} className={r.status === 'failed' ? 'text-red-700' : 'text-gray-700'}>
                  − ₹{formatCurrency(r.amount)} refunded to the wallet · {fmtDateTime(r.at)}
                  {r.status === 'failed' ? ' · NOT PAID OUT - add it from Wallet Management' : ''}
                </li>
              ))}
            </ul>
          )}
          {d.payment_issue && (
            <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">
              Payment issue: {d.payment_issue.issue} (₹{formatCurrency(d.payment_issue.amount_paid)} paid,
              {' '}₹{formatCurrency(d.payment_issue.amount_due)} owed).
            </p>
          )}
        </Section>
      </div>

      {/* Pickup & delivery */}
      {(d.pickup_date || d.delivery_date || d.reschedule_count > 0) && (
        <Section title="Pickup & delivery" accent="border-blue-200">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
            <Field label="Pickup date" value={d.pickup_date ? fmtDateTime(d.pickup_date) : null} />
            <Field label="Delivery date" value={d.delivery_date ? fmtDateTime(d.delivery_date) : null} />
            {d.reschedule_count > 0 && (
              <>
                <Field label="Rescheduled" value={`${d.reschedule_count}×`} />
                <Field label="Previous pickup" value={d.previous_pickup_date ? fmtDateTime(d.previous_pickup_date) : null} />
                <Field label="Rescheduled on" value={d.rescheduled_at ? fmtDateTime(d.rescheduled_at) : null} />
                <Field label="Reason" value={d.reschedule_reason} />
              </>
            )}
          </div>
        </Section>
      )}

      {/* Notes */}
      {d.notes && (
        <Section title="Internal notes">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{d.notes}</p>
        </Section>
      )}

      {/* Changes made since booking */}
      {(d.edit_history || []).length > 0 && (
        <Section title="Changes">
          <ol data-test="edit-history" className="space-y-3">
            {[...d.edit_history].reverse().map((h) => (
              <li key={h.id} className="text-xs">
                <div className="text-gray-500">
                  {fmtDateTime(h.at)} · {h.by_email || h.by}
                  {h.reason ? <span className="text-gray-700"> · “{h.reason}”</span> : null}
                </div>
                <ul className="mt-0.5 space-y-0.5 text-gray-800">
                  {(h.changes || []).map((c) => (
                    <li key={c.field}>
                      {c.label}: <span className="text-gray-500">{shownValue(c.field, c.from, fmtDate)}</span>
                      {' → '}<span className="font-medium">{shownValue(c.field, c.to, fmtDate)}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </Section>
      )}
    </div>
  );
};

// A value from the change history, as a person would read it.
const shownValue = (field, v, fmtDate) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (field === 'final_cost' || field === 'insurance_amount' || field === 'package_info.declared_value') {
    return `₹${formatCurrency(v)}`;
  }
  if (field === 'pickup_date') return fmtDate(v);
  return String(v);
};

export default BookingManagement;
